import { useEffect, useState } from 'react';
import { CampaignHistoryItem } from '../../types/accounts';
import { apiClient } from '../../lib/api';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Props {
  entityId: string;
}

/** Campaigns that touched this entity — targeted and/or replied (SKY-467). */
export function EntityCampaignHistory({ entityId }: Props) {
  const [items, setItems] = useState<CampaignHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    // stale flag: switching entities mid-flight must not let the old response
    // overwrite the new entity's history (same pattern as the portal pages)
    let stale = false;
    setLoading(true);
    setFetchError(false);
    setItems([]);
    apiClient
      .getEntityCampaignHistory(entityId)
      .then((data) => { if (!stale) setItems(data); })
      .catch(() => { if (!stale) setFetchError(true); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [entityId]);

  return (
    <div className="mb-4 border border-neutral-200 rounded-md overflow-hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="campaign-history-content"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-neutral-50 hover:bg-neutral-100 transition"
      >
        <span className="text-13 font-semibold text-neutral-700">
          Campaign History
          {!loading && items.length > 0 && (
            <span className="ml-1.5 text-xs font-normal text-neutral-400">({items.length})</span>
          )}
        </span>
        <span className="text-neutral-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div id="campaign-history-content" className="px-3 py-2 bg-white">
          {loading ? (
            <p className="text-xs text-neutral-400 italic py-1">Loading campaign history…</p>
          ) : fetchError ? (
            <p className="text-xs text-neutral-400 italic py-1">Campaign history unavailable.</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-neutral-400 italic py-1">No campaigns have reached this person yet.</p>
          ) : (
            <ul className="space-y-1.5 py-1">
              {items.map((c) => (
                <li key={c.provider_campaign_id} className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs text-neutral-700 truncate">
                      {c.project_name ?? 'Unregistered campaign'}
                    </span>
                    {c.inbound_replies > 0 && (
                      <span className="text-2xs font-medium text-success-600 bg-success-50 rounded px-1 py-0.5 shrink-0">
                        {c.inbound_replies} repl{c.inbound_replies === 1 ? 'y' : 'ies'}
                      </span>
                    )}
                    {c.targeted && c.inbound_replies === 0 && (
                      <span className="text-2xs font-medium text-neutral-500 bg-neutral-100 rounded px-1 py-0.5 shrink-0">
                        targeted
                      </span>
                    )}
                  </div>
                  <span className="text-2xs text-neutral-400 whitespace-nowrap shrink-0">
                    {c.last_activity_at ? formatRelativeTime(c.last_activity_at) : 'no replies'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
